/**
 * Shared drill-down drawer used by Trial Balance, Balance Sheet, P&L, Schedule III,
 * Registers, Cash Flow, and Segment MIS. Supports 3 modes with a back-stack:
 *   • subcategory: list of accounts under a Sch III bucket
 *   • account: full ledger with voucher entries
 *   • voucher: full voucher detail
 * Each mode is pushed onto a stack so the user can navigate back one level.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ChevronLeft, X } from "lucide-react";

// Shared context — any component under <DrillCtx.Provider> from useDrillDown() can consume this.
export const DrillCtx = createContext(null);

/** Consume the drill-down API safely — returns a no-op object when outside a provider. */
export function useOptionalDrill() {
  const ctx = useContext(DrillCtx);
  return ctx || { openAccount: () => {}, openSubcategory: () => {}, openVoucher: () => {}, openVouchers: () => {}, openVoucherByNumber: () => {} };
}

export function EntryChip({ count, testid }) {
  if (!count || count === 0) return null;
  return (
    <span
      className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-muted/60 text-[9px] font-mono text-muted-foreground group-hover:bg-accent/20 group-hover:text-accent transition-colors"
      title={`${count} voucher line${count === 1 ? "" : "s"} — click row to drill down`}
      data-testid={testid}
    >
      {count}
    </span>
  );
}

/**
 * Wrap any cell/row to make it clickable → opens drill-down.
 * Adds hover + underline affordance so users know it's interactive.
 */
export function DrillCell({ children, onClick, className = "", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left w-full hover:text-accent hover:underline decoration-dotted underline-offset-2 transition-colors ${className}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

export function DrillDownDrawer({ stack, onPop, onClose, onPush }) {
  const top = stack[stack.length - 1];
  if (!top) return null;
  return (
    <Sheet open={stack.length > 0} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl rounded-none overflow-y-auto"
        aria-describedby={undefined}
        data-testid="drilldown-drawer"
      >
        <SheetHeader className="border-b pb-3 mb-4">
          <div className="flex items-center gap-2">
            {stack.length > 1 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onPop}
                className="rounded-sm h-7 gap-1"
                data-testid="drilldown-back"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {top.kind === "subcategory" ? "Sub-category breakdown" :
                 top.kind === "vouchers" ? "Vouchers" :
                 top.kind === "account" ? "Ledger" : "Voucher"}
                {stack.length > 1 && (
                  <span className="ml-2 opacity-50">
                    · {stack.map((s, i) => (
                      <span key={i}>{i > 0 && " › "}{s.crumb}</span>
                    ))}
                  </span>
                )}
              </div>
              <SheetTitle className="font-heading truncate">{top.title}</SheetTitle>
              <SheetDescription className="sr-only">
                {top.kind === "subcategory" && "List of contributing accounts under this sub-category. Click a row to open its ledger."}
                {top.kind === "vouchers" && "Vouchers contributing to this category. Click a row to see full voucher detail."}
                {top.kind === "account" && "Full ledger of voucher entries. Click a row to see the underlying voucher."}
                {top.kind === "voucher" && "Full voucher detail with all lines."}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        {top.kind === "subcategory" && <SubcategoryPane node={top} onPush={onPush} />}
        {top.kind === "vouchers" && <VoucherListPane node={top} onPush={onPush} />}
        {top.kind === "account" && <AccountLedgerPane node={top} onPush={onPush} />}
        {top.kind === "voucher" && <VoucherPane node={top} />}
      </SheetContent>
    </Sheet>
  );
}

function SubcategoryPane({ node, onPush }) {
  const { rows = [] } = node;
  if (rows.length === 0) return <div className="text-center py-8 text-muted-foreground">No accounts in this bucket.</div>;
  return (
    <table className="w-full dense" data-testid="drill-sub-table">
      <thead>
        <tr>
          <th style={{ width: 80 }}>Code</th>
          <th>Account</th>
          <th className="text-right" style={{ width: 160 }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.account_id || r.code}
            className={r.account_id ? "cursor-pointer hover:bg-muted/40" : "opacity-60"}
            onClick={() => {
              if (!r.account_id) return;
              onPush({
                kind: "account",
                account_id: r.account_id,
                title: `${r.code} · ${r.name}`,
                crumb: r.name,
              });
            }}
            data-testid={`drill-sub-row-${r.code}`}
          >
            <td className="font-mono text-xs">{r.code}</td>
            <td>
              {r.name}
              <EntryChip count={r.entry_count} testid={`entry-chip-${r.code}`} />
            </td>
            <td className="text-right tabular-nums font-mono">{fmtINR(r.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VoucherListPane({ node, onPush }) {
  const { vouchers = [], total_amount } = node;
  if (vouchers.length === 0) return <div className="text-center py-8 text-muted-foreground">No vouchers found.</div>;
  return (
    <>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
        {vouchers.length} voucher{vouchers.length === 1 ? "" : "s"} · net {fmtINR(total_amount || 0)}
      </div>
      <table className="w-full dense" data-testid="drill-voucher-list">
        <thead>
          <tr>
            <th>Date</th>
            <th>Voucher</th>
            <th>Type</th>
            <th>Narration</th>
            <th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {vouchers.map((v, i) => (
            <tr
              key={v.voucher_id}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onPush({
                kind: "voucher", voucher_id: v.voucher_id,
                title: v.voucher_number, crumb: v.voucher_number,
              })}
              data-testid={`drill-vlist-row-${i}`}
            >
              <td className="text-xs">{v.date}</td>
              <td className="font-mono text-xs text-accent hover:underline">{v.voucher_number}</td>
              <td className="text-[10px] font-mono uppercase text-muted-foreground">{v.voucher_type}</td>
              <td className="text-xs truncate max-w-[240px]" title={v.narration}>{v.narration || "—"}</td>
              <td className={`text-right tabular-nums font-mono ${v.amount >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtINR(v.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}


function AccountLedgerPane({ node, onPush }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/accounts/${node.account_id}/ledger`, {
      params: {
        ...(node.from_date ? { from_date: node.from_date } : {}),
        ...(node.to_date ? { to_date: node.to_date } : {}),
      },
    }).then((r) => setData(r.data));
  }, [node.account_id, node.from_date, node.to_date]);
  if (!data) return <div className="text-center py-8 text-muted-foreground">Loading ledger…</div>;
  return (
    <>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
        {data.entries.length} entries · closing {fmtINR(data.closing_balance)}
      </div>
      <table className="w-full dense" data-testid="drill-ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Voucher</th>
            <th>Narration</th>
            <th className="text-right">Debit</th>
            <th className="text-right">Credit</th>
            <th className="text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.length === 0 && (
            <tr><td colSpan="6" className="text-center text-muted-foreground py-8">No entries yet.</td></tr>
          )}
          {data.entries.map((e, i) => (
            <tr
              key={i}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onPush({
                kind: "voucher",
                voucher_id: e.voucher_id,
                title: `${e.voucher_number}`,
                crumb: e.voucher_number,
              })}
              data-testid={`drill-ledger-row-${i}`}
            >
              <td className="text-xs">{e.date}</td>
              <td className="font-mono text-xs text-accent hover:underline">{e.voucher_number}</td>
              <td className="text-xs">{e.narration || "—"}</td>
              <td className="text-right tabular-nums">{e.debit ? fmtINR(e.debit) : "—"}</td>
              <td className="text-right tabular-nums">{e.credit ? fmtINR(e.credit) : "—"}</td>
              <td className="text-right tabular-nums font-mono">{fmtINR(e.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function VoucherPane({ node }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    api.get(`/vouchers/${node.voucher_id}`).then((r) => setV(r.data));
  }, [node.voucher_id]);
  if (!v) return <div className="text-center py-8 text-muted-foreground">Loading voucher…</div>;
  return (
    <div data-testid="drill-voucher-detail">
      <div className="grid grid-cols-2 gap-2 text-xs mb-4 pb-3 border-b">
        <div><span className="text-muted-foreground uppercase text-[10px]">Type · </span><span className="font-mono uppercase">{v.voucher_type}</span></div>
        <div><span className="text-muted-foreground uppercase text-[10px]">Date · </span><span className="font-mono">{v.date}</span></div>
        <div><span className="text-muted-foreground uppercase text-[10px]">Status · </span><span className="font-mono uppercase">{v.status}</span></div>
        <div><span className="text-muted-foreground uppercase text-[10px]">Ref · </span><span className="font-mono">{v.reference || "—"}</span></div>
        {v.narration && <div className="col-span-2"><span className="text-muted-foreground uppercase text-[10px]">Narration · </span>{v.narration}</div>}
      </div>
      <table className="w-full dense" data-testid="drill-voucher-lines">
        <thead>
          <tr>
            <th>Account</th>
            <th>Narration</th>
            <th className="text-right">Debit</th>
            <th className="text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {(v.lines || []).map((l, i) => (
            <tr key={i}>
              <td>
                <span className="font-mono text-xs text-muted-foreground">{l.account_code}</span>{" "}
                {l.account_name}
              </td>
              <td className="text-xs text-muted-foreground">{l.narration || "—"}</td>
              <td className="text-right tabular-nums">{l.debit ? fmtINR(l.debit) : "—"}</td>
              <td className="text-right tabular-nums">{l.credit ? fmtINR(l.credit) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2">
            <td colSpan="2" className="text-right pt-2 font-semibold">Total</td>
            <td className="text-right tabular-nums pt-2 font-mono">{fmtINR(v.total_debit)}</td>
            <td className="text-right tabular-nums pt-2 font-mono">{fmtINR(v.total_credit)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---- Public hook -----------------------------------------------------------
/**
 * const drill = useDrillDown();
 * drill.openAccount(account_id, "Cash in Hand", {from_date, to_date});
 * drill.openSubcategory("Trade Receivables", rows);
 * drill.openVoucher(voucher_id, "SI/2026/000123");
 * <drill.Render />
 */
export function useDrillDown() {
  const [stack, setStack] = useState([]);
  const openAccount = useCallback((account_id, title, { from_date, to_date } = {}) => {
    setStack([{ kind: "account", account_id, title, crumb: title, from_date, to_date }]);
  }, []);
  const openSubcategory = useCallback((title, rows) => {
    setStack([{ kind: "subcategory", title, rows, crumb: title }]);
  }, []);
  const openVoucher = useCallback((voucher_id, title) => {
    setStack([{ kind: "voucher", voucher_id, title, crumb: title }]);
  }, []);
  const openVouchers = useCallback((title, vouchers, total_amount) => {
    setStack([{ kind: "vouchers", title, vouchers, total_amount, crumb: title }]);
  }, []);
  const openVoucherByNumber = useCallback(async (voucher_number) => {
    try {
      const r = await api.get("/vouchers", { params: { voucher_number, limit: 1 } });
      const v = (r.data || [])[0];
      if (v) setStack([{ kind: "voucher", voucher_id: v.id, title: v.voucher_number, crumb: v.voucher_number }]);
    } catch (e) { /* silent — nothing to open */ }
  }, []);
  const push = useCallback((node) => setStack((s) => [...s, node]), []);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const close = useCallback(() => setStack([]), []);
  const value = { openAccount, openSubcategory, openVoucher, openVouchers, openVoucherByNumber };
  return {
    ...value,
    _stack: stack, _pop: pop, _close: close, _push: push, _value: value,
  };
}

/** Standalone <Provider> wrapper — pass the object returned by useDrillDown(). */
export function DrillProvider({ drill, children }) {
  return (
    <DrillCtx.Provider value={drill._value}>
      {children}
      <DrillDownDrawer stack={drill._stack} onPop={drill._pop} onClose={drill._close} onPush={drill._push} />
    </DrillCtx.Provider>
  );
}
